import * as React from 'react'
import {DropdownMenu, palette} from '@conserveit/blocks'
import {useState as useWorkflowState} from '../../../context/reports/workflow'
import {SingleBar} from './types'
import * as Chart from '../ChartZone.styles'
import {
  GroupedMetrics, Metrics,
  MetricsByType,
  MetricsType,
  State,
  Summary
} from '../../../context/reports/workflow/types'
import {navigate} from '../../../utils/routes/navigate'
import {getReadableMetricsType} from './utils'
import {BY_TYPE_OPTIONS} from '../utils'
import styled from 'styled-components'
import {RankingBarChart} from '../../Charts/components/RankingBarChart'
import {ChartMargin} from '../../Layout/Page'
import {getChartConfig} from './getChartConfig'
import {chartClickEvent} from '../../../utils/eventTracking'
import {TRACKING_EVENT_CHART_CLICK} from '../../../context/reports/workflow/constants'
import {useTrackedActions} from './hooks/useTrackedActions'
import {Sorting} from '../../../context/reports/types'
import {NoResult} from '../'
import {ChartLoader} from '../../Charts/components/ChartLoader'
import {Series} from '../Utilities/types'
import {COLORS_RAW} from "../../AppChrome/App.constants";
import {WorkflowDatagrid} from "./components";
import {DatagridWrapper} from "./Workflow.styles";
import {GridProps} from "./components/WorkflowDatagrid";
import EventEmitter from '../../../utils/eventEmitter';

interface Props {
  bars: SingleBar[]
  eventEmitter: EventEmitter
}

/** Shape of the drilldown series processData builds, and the event that selects one. */
export interface DrilldownSeries {
  type: string;
  name: string;
  id: string;
  data: Array<{ name: string; y: number }>;
}

export interface DrilldownEvent {
  seriesOptions?: { name?: string };
  // The drilldown events carry more than is used here; kept loose so callers
  // can pass a fuller object through.
  [key: string]: any;
}

interface PortfolioData {
  series: Series[];
  drilldowndata: DrilldownSeries[];
}


const chartTitle = 'Portfolio workflow performance'

interface Datum {
  name: string
  value: number
}

const VALUE_SUFFIX = ' actions closed'
const formatTooltipValue = (value: number) => `${Math.round(value)}${VALUE_SUFFIX}`
const formatValueLabel = (value: number) => `${value}`

export const processData = (
  direction: Sorting,
  group: MetricsByType,
  portfolioMetrics: Metrics,
  summary: Summary
): PortfolioData => {
  const sortFactor = direction === Sorting.Desc ? 1 : -1
  let categories: string[] = []
  let series: Series[] = []
  let drilldowndata: DrilldownSeries[] = []
  let chartRows: any[] = []
  let seriesName: string
  if (group === MetricsByType.BySite) {
    let bySiteMetrics = portfolioMetrics.bySite
    Object.keys(bySiteMetrics).forEach(site => {
      // for by site we want the total count and the site name
      let siteName = getSiteName(site, summary)
      chartRows.push({ name: siteName, y: bySiteMetrics[site].count, drilldown: siteName })
      drilldowndata.push({ type: 'column', name: siteName, id: siteName, data: processDrilldownData(bySiteMetrics, site, sortFactor) })
    })
    seriesName = 'Sites'
  } else {
    let byRoleMetrics = portfolioMetrics.byResponsible
    Object.keys(byRoleMetrics).forEach(role => {
      chartRows.push({ name: role, y: byRoleMetrics[role].count, drilldown: role })
      drilldowndata.push({ type: 'column', name: role, id: role, data: processDrilldownData(byRoleMetrics, role, sortFactor) })
    })
    seriesName = 'Teams'
  }
  chartRows.sort((a,b) => sortFactor * (b.y - a.y) || a.name.localeCompare(b.name))
  chartRows.forEach(row => {
    categories.push(row.name)
  })
  series.push({ name: seriesName, data: chartRows })

  return { series, drilldowndata }
}

const processDrilldownData = (
  sitesData: GroupedMetrics,
  siteKey: string,
  direction: number
): any[] => {
  let drilldownData: any[] = []
  Object.keys(sitesData[siteKey].breakdown).forEach(row => {
    let roleData = sitesData[siteKey].breakdown[row]
    drilldownData.push({ name: roleData.name, y: roleData.count })
  })
  drilldownData.sort((a,b) =>
    direction * (b.y - a.y) | a.name.localeCompare(b.name)
  )
  return drilldownData
}

export const getSiteName = (
  id: string,
  portfolioSummary: Summary
): string => {
  const sites = portfolioSummary.bySite;
  if(sites[id]) {
    return sites[id].name;
  }
  else {
    return "undefined";
  }
}

export const populateGridData = (
  state?: State,
  eventObj?: DrilldownEvent
): GridProps => {
  if (eventObj === undefined || state === undefined || eventObj.seriesOptions === undefined) return { records: [], count: 0, critical: 0 }
  const siteName = eventObj.seriesOptions.name
  let gridData: GridProps = { records: [], count: 0, critical: 0 }
  if (state.byType === MetricsByType.BySite) {
    // populate grid with roles
    let id: string = ''
    let sites = state.sites
    sites.forEach(site => {
      if (site.siteName === siteName) {
        id = site.siteId
        return
      }
    })
    if (id === '') return { records: [], count: 0, critical: 0 }
    let siteMetrics = state.metrics.completed.bySite
    gridData.count = siteMetrics[id].count
    gridData.critical = siteMetrics[id].critical
    let breakdown = siteMetrics[id].breakdown
    Object.keys(breakdown).forEach(role => {
      let label = breakdown[role].name
      let totalCount = breakdown[role].count
      let criticalCount = breakdown[role].critical
      let actionIds = breakdown[role].actionIds
      gridData.records.push({ label, totalCount, criticalCount, actionIds })
    })
  } else {
    return { records: [], count: 0, critical: 0 }
  }
  return gridData
}

/**
 * The rollup shown before any drilldown: one row per site (or per team),
 * so the table is populated at the top level rather than only after drilling.
 */
export const buildOverviewGrid = (
  grouped: GroupedMetrics,
  byType: MetricsByType,
  summary: Summary
): GridProps => {
  const records = Object.keys(grouped).map((key) => ({
    label: byType === MetricsByType.BySite ? getSiteName(key, summary) : key,
    totalCount: grouped[key].count,
    criticalCount: grouped[key].critical
  }))

  return {
    records,
    count: records.reduce((sum, record) => sum + record.totalCount, 0),
    critical: records.reduce((sum, record) => sum + record.criticalCount, 0)
  }
}

const DrillUpBar = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 20px;
`

const DrillUpButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid ${COLORS_RAW.GREY_004};
  border-radius: 4px;
  background: ${COLORS_RAW.PURE_WHITE};
  color: ${COLORS_RAW.INK};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${COLORS_RAW.GREY_001};
    border-color: ${COLORS_RAW.GREY_005};
  }
`

export const WFPortfolio: React.FC<Props> = ({ bars , eventEmitter = new EventEmitter() }) => {
  const state = useWorkflowState()
  const { trackedOrderBy, trackedGroupBy } = useTrackedActions()
  const barColor = {
    base: palette.cyan8,
    hover: palette.cyan9
  }

  const byTypeOptions = React.useMemo(() => Object.values(BY_TYPE_OPTIONS), [])

  const firstBarBatch = {
    color: barColor.base,
    bars
  }
  const data = [firstBarBatch/*, secondBarBatch*/]
  const { series, drilldowndata } = processData(state.sortingDirection, state.byType, state.metrics[MetricsType.Completed], state.summary)

  const stateRef = React.useRef(state)
  stateRef.current = state

  const overviewGrid = React.useMemo(
    () => buildOverviewGrid(
      state.metrics[MetricsType.Completed][state.byType],
      state.byType,
      state.summary
    ),
    [state.metrics, state.byType, state.summary]
  )

  // Recharts has no drilldown, so the level is tracked here. `drilled` holds
  // the clicked category name, which is also the id processData gives each
  // drilldown series.
  const [drilled, setDrilled] = React.useState<string | null>(null)

  const topLevel: Datum[] = ((series[0]?.data as any[]) ?? []).map((row) => ({
    name: row.name,
    value: row.y
  }))
  const drilledLevel: Datum[] = drilled
    ? (((drilldowndata.find((d: any) => d.id === drilled) as any)?.data as any[]) ?? []).map((point) => ({
        name: point.name,
        value: point.y
      }))
    : []
  const chartData = drilled ? drilledLevel : topLevel

  // The datagrid seeds from props once and then only listens to the emitter,
  // so every level change has to be pushed through it.
  React.useEffect(() => {
    const next = drilled
      ? populateGridData(
          stateRef.current,
          { seriesOptions: { name: drilled } }
        )
      : overviewGrid
    eventEmitter.fire('updateGrid', next)
  }, [drilled, overviewGrid, eventEmitter])

  const handleDrillDown = React.useCallback((datum: Datum) => {
    setDrilled(datum.name)
  }, [])

  const handleDrillUp = React.useCallback(() => {
    setDrilled(null)
  }, [])
  React.useMemo(() => {
    const baseConfig = getChartConfig(data, {
      from: state.startDate,
      to: state.endDate,
      valueLabel: <span>ACTIONS <em>{getReadableMetricsType(state.metrics.currentMetricsType).toUpperCase()}</em></span>
    })
    return {
      ...baseConfig,
      events: {
        dataPlotClick: function (_e: any, d: any) {
          // Tracking
          const bar = data[0].bars[d.index]

          chartClickEvent({
            chartTitle,
            d,
            bars: data[0].bars,
            sortDir: state.sortingDirection
          }, TRACKING_EVENT_CHART_CLICK)

          // Navigation
          let path
          const id = bar.id
          if (state.byType === MetricsByType.BySite) {
            path = `/reports/performance/workflow/site/${id}`
          } else if (state.byType === MetricsByType.ByResponsible) {
            path = `/reports/performance/workflow/team/${id}`
          } else {
            throw Error('Invalid group type')
          }
          navigate(path)()
        }
      }
    }
  }, [state.byType, bars])

  const portfolioCount = state.summary[state.metrics.currentMetricsType]
  // Matches whatever level is on screen rather than always the portfolio.
  const count = drilled
    ? chartData.reduce((sum, datum) => sum + datum.value, 0)
    : portfolioCount
  return (
    <>
      <Chart.Container>
        <Chart.Block>
          <Chart.Header>
            <Chart.Heading>
              <Chart.Title>
                {chartTitle}
              </Chart.Title>
              <Chart.Subtitle>
                Shown as number of Actions <em>{getReadableMetricsType(state.metrics.currentMetricsType)}</em> ({count})
              </Chart.Subtitle>
            </Chart.Heading>
            <Chart.Controls>
              <DropdownMenu
                right
                trigger='Group By'
                defaultValue={state.byType}
                value={state.byType}
                onSelect={trackedGroupBy(chartTitle)}
                options={byTypeOptions}
              />
              <DropdownMenu
                right
                trigger='Order'
                defaultValue={state.sortingDirection}
                value={state.sortingDirection}
                onSelect={trackedOrderBy(chartTitle)}
                options={[
                  { value: Sorting.Desc, label: 'Most to Least' },
                  { value: Sorting.Asc, label: 'Least to Most' }
                ]}
              />
            </Chart.Controls>
          </Chart.Header>
          <Chart.Content>
            { state.metricsLoading ? (<ChartLoader />) : portfolioCount !== 0 ? (
              <>
                {drilled && (
                  <DrillUpBar>
                    <DrillUpButton onClick={handleDrillUp}>
                      &#9665; Back to {series[0]?.name ?? 'Sites'}
                    </DrillUpButton>
                  </DrillUpBar>
                )}
                <ChartMargin>
                  <RankingBarChart
                    data={chartData}
                    color={COLORS_RAW.TEAL_001}
                    xAxisTitle="Actions closed"
                    tooltipFormatter={formatTooltipValue}
                    valueLabelFormatter={formatValueLabel}
                    onBarClick={drilled ? undefined : handleDrillDown}
                  />
                </ChartMargin>
                <DatagridWrapper>
                  <WorkflowDatagrid
                    trackingTitle={chartTitle}
                    groupRowLabel={!drilled && state.byType === MetricsByType.BySite ? 'Site' : 'Team'}
                    records={overviewGrid.records}
                    count={overviewGrid.count}
                    critical={overviewGrid.critical}
                    eventEmitter = {eventEmitter}
                  />
                </DatagridWrapper>
              </>
              ) : (
                <NoResult />
            )}
          </Chart.Content>
        </Chart.Block>
      </Chart.Container>
    </>
  )
}
